// /login — SSO sign-in entry point (T337).
//
// The SPA has no session of its own to offer: every provider is a redirect
// to the backend, which redirects on to the IdP (OIDC/SAML) or serves its
// own credential form (LDAP). This page's only job is discovery — probe the
// three `/auth/{oidc,saml,ldap}/info` endpoints and render a button for
// whichever ones the org has configured (a 404 means "not enabled"; there is
// no unified list-providers endpoint, see each *Handlers.InfoHandler in
// api/internal/handlers).
//
// Rendered outside <Layout> (see App.tsx) so it doesn't show the
// authenticated top nav while the visitor has no session yet.

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, LogIn, ShieldCheck } from 'lucide-react';
import { apiURL } from '../lib/api';
import { setToken } from '../lib/auth';
import { Button, Card, FormField, Input } from '../components/ui';

// Stable error codes the SAML ACS handler (api/internal/handlers/saml.go)
// redirects here with on a failed login (T546) — the browser arrives
// mid-navigation from the IdP's POST, so these can't be rendered as a JSON
// body the way a normal API error would be. Keep this map's keys in sync
// with ACSHandler's redirectLoginError call sites; an unrecognized code (an
// older backend build, or a future addition this map hasn't caught up with
// yet) falls back to a generic message rather than showing nothing.
const SSO_ERROR_MESSAGES: Record<string, string> = {
  saml_form_invalid: 'The identity provider sent a response we could not read. Please try signing in again.',
  saml_artifact_binding_unsupported: 'This identity provider is configured to use an unsupported SAML binding. Contact your administrator.',
  saml_response_invalid: 'Sign-in failed: the identity provider response could not be validated.',
  insufficient_claims: 'Your identity provider did not send the information required to sign you in. Contact your administrator.',
  idp_initiated_disabled: 'Signing in directly from your identity provider is not enabled. Start the login from this page instead.',
  user_error: 'We could not create or look up your account. Please try again or contact your administrator.',
  jwt_error: 'We could not start your session. Please try again.',
  // T615 gates SSO session issuance on the "sso" FeatureSet; the ACS handler
  // redirects here with this code when the org's licence doesn't include it.
  // Without this entry the user only saw the generic fallback and had no way
  // to tell a licence block from a genuine IdP failure.
  //
  // T690: this used to say "sign in with a local account" — orbital-lint has
  // no local password login anywhere, so that instruction was impossible to
  // follow and left operators with no real path back in. The actual recovery
  // path is the break-glass ORBITALLINT_BOOTSTRAP_ADMIN_TOKEN env var
  // (docs/INSTALL.md's "Locked out" section), which an operator sets on the
  // server — there is nothing to click from this page.
  feature_locked: 'Single sign-on is not included in your current licence. An administrator with server access can recover using the break-glass ORBITALLINT_BOOTSTRAP_ADMIN_TOKEN procedure documented in docs/INSTALL.md, then upload a licence (or start a trial) under Admin → License.',
};

function ssoErrorMessage(code: string): string {
  return SSO_ERROR_MESSAGES[code] ?? 'Sign-in failed. Please try again or contact your administrator.';
}

interface Provider {
  readonly id: 'oidc' | 'saml' | 'ldap';
  readonly label: string;
  readonly icon: typeof LogIn;
  readonly loginUrl: string;
}

const PROVIDER_META: Record<Provider['id'], { label: string; icon: typeof LogIn }> = {
  oidc: { label: 'Sign in with OIDC', icon: LogIn },
  saml: { label: 'Sign in with SAML', icon: ShieldCheck },
  ldap: { label: 'Sign in with LDAP / Active Directory', icon: KeyRound },
};

async function probeProvider(id: Provider['id']): Promise<Provider | null> {
  try {
    const res = await fetch(apiURL(`/auth/${id}/info`), { headers: { Accept: 'application/json' } });
    if (!res.ok) return null; // 404 (or anything else) → not configured
    const body = (await res.json()) as { login_url?: string };
    if (!body.login_url) return null;
    return { id, loginUrl: body.login_url, ...PROVIDER_META[id] };
  } catch {
    // Network error probing a single provider shouldn't blank the whole page.
    return null;
  }
}

// probeLocalAvailable checks GET /auth/local/info (T9001). Unlike the three
// SSO providers, there is no login_url to redirect to — a 200 just means the
// operator has set ORBITALLINT_BOOTSTRAP_ADMIN_EMAIL/_PASSWORD, so the inline
// email+password form below should render. A 404 (the default, unconfigured
// state) means "not available", same convention as probeProvider.
async function probeLocalAvailable(): Promise<boolean> {
  try {
    const res = await fetch(apiURL('/auth/local/info'), { headers: { Accept: 'application/json' } });
    return res.ok;
  } catch {
    return false;
  }
}

export function Login() {
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [localAvailable, setLocalAvailable] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const errorCode = searchParams.get('error');

  useEffect(() => {
    let cancelled = false;
    Promise.all((['oidc', 'saml', 'ldap'] as const).map(probeProvider)).then((results) => {
      if (cancelled) return;
      setProviders(results.filter((p): p is Provider => p !== null));
    });
    probeLocalAvailable().then((available) => {
      if (!cancelled) setLocalAvailable(available);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <Card className="w-full max-w-sm p-8">
        <p className="orb-eyebrow mb-2">orbital · lint</p>
        <h1 className="font-display text-2xl font-semibold text-fg mb-6">Sign in</h1>

        {errorCode ? (
          <p role="alert" className="text-sm text-danger mb-6">
            {ssoErrorMessage(errorCode)}
          </p>
        ) : null}

        <LoginProviders
          providers={providers}
          localAvailable={localAvailable}
          onLocalSuccess={() => navigate('/', { replace: true })}
        />
      </Card>
    </div>
  );
}

function LoginProviders({
  providers,
  localAvailable,
  onLocalSuccess,
}: {
  readonly providers: Provider[] | null;
  readonly localAvailable: boolean;
  readonly onLocalSuccess: () => void;
}) {
  if (providers === null) {
    return <p className="text-sm text-fg-muted">Checking available sign-in methods…</p>;
  }
  if (providers.length === 0) {
    // T9001: no SSO provider configured is no longer a dead end when a local
    // break-glass account exists — the form is pre-selected in that case
    // instead of leaving the operator with only an explanatory message.
    if (localAvailable) {
      return <LocalLoginForm onSuccess={onLocalSuccess} />;
    }
    return (
      <p className="text-sm text-fg-muted">
        No SSO provider is configured for this instance. Ask an admin to enable OIDC, SAML, or
        LDAP under Admin → SSO, or set <code className="text-xs">VITE_DEV_ORG_ID</code> for local
        development.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {providers.map(({ id, label, icon: Icon, loginUrl }) => (
        <Button
          key={id}
          variant="secondary"
          className="justify-start normal-case tracking-normal text-sm font-medium"
          onClick={() => {
            // Full navigation, not react-router: this is a redirect to the
            // backend (and from there, the IdP or an LDAP form), not a
            // client-side route.
            globalThis.location.assign(apiURL(loginUrl));
          }}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </Button>
      ))}

      {/* T9001: a second area next to the provider buttons — the local
          break-glass account is always an option, not just a fallback for
          when every provider above is unconfigured. */}
      {localAvailable ? (
        <div className="pt-2 mt-1 border-t border-border">
          <p className="orb-section-title mt-3 mb-2">Or sign in with a local account</p>
          <LocalLoginForm onSuccess={onLocalSuccess} />
        </div>
      ) : null}
    </div>
  );
}

function LocalLoginForm({ onSuccess }: { readonly onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(apiURL('/auth/local/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = (await res.json().catch(() => null)) as { token?: string; error?: string } | null;
      if (!res.ok) {
        setError(body?.error ?? 'Sign-in failed. Please try again.');
        return;
      }
      if (!body?.token) {
        setError('No session token was returned.');
        return;
      }
      setToken(body.token);
      onSuccess();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <FormField label="Email" htmlFor="local-login-email">
        <Input
          id="local-login-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormField>
      <FormField label="Password" htmlFor="local-login-password">
        <Input
          id="local-login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </FormField>
      <Button type="submit" loading={submitting} className="normal-case tracking-normal text-sm font-medium">
        <KeyRound className="h-4 w-4" aria-hidden="true" />
        Sign in
      </Button>
    </form>
  );
}
